"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Download, Youtube, AlertCircle, Video, Music } from 'lucide-react'

const YouTubeDownloader = () => {
  const [url, setUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [videoInfo, setVideoInfo] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setVideoInfo(null)

    const myHeaders = new Headers()
    myHeaders.append("x-apihub-key", "dC-X6X1n75W9ul7oEz2tOmvuaww1WLB3cGiKWsfsq057qO4-Ay")
    myHeaders.append("x-apihub-host", "Video-Downloader.allthingsdev.co")
    myHeaders.append("x-apihub-endpoint", "61cd9229-ed75-4f09-84ed-5da30e4881be")

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    }

    try {
      const response = await fetch(`https://Video-Downloader.proxy-production.allthingsdev.co/youtube-video-downloader?url=${encodeURIComponent(url)}`, requestOptions)
      const result = await response.json()

      if (response.ok) {
        setVideoInfo(result)
      } else {
        throw new Error(result.message || "Failed to fetch video information")
      }
    } catch (error) {
      console.error("Error:", error)
      setError(error.message || "An error occurred while processing your request")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <Youtube className="inline-block text-red-600 mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">YouTube Video Downloader</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Download your favorite YouTube videos easily!
        </p>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="url"
            placeholder="Paste YouTube video URL here"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            className="flex-grow px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-colors duration-300 flex items-center justify-center"
          >
            {loading ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              <>
                <Download className="mr-2" size={18} />
                Download
              </>
            )}
          </button>
        </div>
      </motion.form>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-8"
          role="alert"
        >
          <div className="flex items-center">
            <AlertCircle className="mr-2" size={18} />
            <p>{error}</p>
          </div>
        </motion.div>
      )}

      {videoInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4 dark:text-white">{videoInfo.title}</h2>
            <img src={videoInfo.imageUrl || "/placeholder.svg"} alt="Video Thumbnail" className="w-full rounded-lg mb-4" />
            <div className="grid gap-4">
              <h3 className="text-xl font-semibold dark:text-white">Video Downloads</h3>
              {videoInfo.downloadLinks.filter(link => !link.hasVolume).map((link, index) => (
                <a
                  key={index}
                  href={link.link}
                  download
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-300 flex items-center justify-center"
                >
                  <Video className="mr-2" size={18} />
                  Download {link.resolution}
                </a>
              ))}
              <h3 className="text-xl font-semibold mt-4 dark:text-white">Audio Downloads</h3>
              {videoInfo.downloadLinks.filter(link => link.hasVolume).map((link, index) => (
                <a
                  key={index}
                  href={link.link}
                  download
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors duration-300 flex items-center justify-center"
                >
                  <Music className="mr-2" size={18} />
                  Download {link.resolution}
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default YouTubeDownloader
